import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ActivityChip from "@/components/ActivityChip";
import NotificationBell from "@/components/NotificationBell";
import { TID } from "@/lib/testIds";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Menu, X, LogOut, LayoutDashboard, Users, Globe, GraduationCap,
  DollarSign, Search, CalendarClock, ChevronDown,
} from "lucide-react";

/**
 * iter94 (Cohort P1) — Nav redesign.
 *
 * Before: 4 public links (Countries · Universities · Partnerships · Pricing)
 *   were always visible on desktop, which made the bar feel cluttered next to
 *   the logged-in state (Cohort · Assembly · Dashboard · Bell · Log out).
 *
 * Now: the three discovery links collapse into an "Explore" dropdown; only
 *   Pricing stays as a top-level link (high-intent, short list). Mobile
 *   hamburger still lists everything flat — no hidden UX on small screens.
 *
 * Also: "A Nikshala AI product" tagline shrunk + repositioned to sit next to
 *   the logo rather than under it, so the nav is a single row on every viewport
 *   with no vertical bleed.
 */

const exploreLinks = [
  { to: "/countries",    label: "Countries",    icon: Globe,         testid: TID.navCountries },
  { to: "/universities", label: "Universities", icon: GraduationCap, testid: "nav-universities" },
  { to: "/partnerships", label: "Partnerships", icon: Search,        testid: "nav-partnerships" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-1.5 text-[13.5px] font-medium whitespace-nowrap transition-colors ${
      isActive ? "text-elec" : "text-ink/85 hover:text-ink"
    }`;

  const isInternal = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/handler");

  return (
    <>
    <nav className="fixed inset-x-0 top-0 z-50 border-b border-ink/12 bg-paper/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 py-3.5">
        {/* Logo + calm inline attribution */}
        <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-2.5 group leading-none">
          <Logo />
          <span
            data-testid="nav-nikshala-attribution"
            className="hidden md:inline text-[10px] tracking-[0.14em] text-muted font-medium uppercase whitespace-nowrap border-l border-ink/15 pl-2.5"
          >
            A Nikshala AI product
          </span>
        </Link>

        {/* desktop nav — condensed */}
        <div className="hidden lg:flex items-center gap-5 xl:gap-6">
          {/* Explore dropdown consolidates the 3 discovery routes */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-explore-trigger"
              className="flex items-center gap-1 text-[13.5px] font-medium text-ink/85 hover:text-ink transition-colors outline-none focus-visible:text-ink"
            >
              Explore <ChevronDown size={14} strokeWidth={2.2} className="text-ink/50"/>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={10}
              data-testid="nav-explore-menu"
              className="min-w-[190px] rounded-xl border border-ink/12 bg-paper shadow-lg p-1.5"
            >
              {exploreLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <NavLink
                    to={l.to}
                    data-testid={l.testid}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium text-ink/85 hover:text-ink hover:bg-ink/[0.04] cursor-pointer"
                  >
                    <l.icon size={14} strokeWidth={2} className="text-elec"/> {l.label}
                  </NavLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/pricing" data-testid={TID.navPricing} className={linkCls}>
            <DollarSign size={14} strokeWidth={2}/> Pricing
          </NavLink>

          {user && user.role === "student" && (
            <NavLink to="/dashboard/manifest" data-testid="nav-manifest" className={linkCls}>
              <Users size={14} strokeWidth={2}/> Cohort
            </NavLink>
          )}
          {user && user.role === "student" && (
            <NavLink to="/dashboard/moments" data-testid="nav-moments" className={linkCls}>
              <CalendarClock size={14} strokeWidth={2}/> Assembly
            </NavLink>
          )}
          {user && user.role === "student" && <ActivityChip />}

          {user ? (
            <>
              {user.role === "student" && (
                <NavLink
                  to="/dashboard"
                  data-testid={TID.navDashboard}
                  className="moe-btn moe-btn-ghost !py-1.5 !px-3 text-[13px] whitespace-nowrap"
                >
                  <LayoutDashboard size={13}/> Dashboard
                </NavLink>
              )}
              {user.role === "student" && <NotificationBell/>}
              {user.role === "admin"        && <NavLink to="/admin"   className={linkCls}>Admin</NavLink>}
              {user.role === "case_handler" && <NavLink to="/handler" className={linkCls}>Handler</NavLink>}
              <button
                data-testid={TID.navLogout}
                onClick={logout}
                className="text-[13px] font-medium text-ink/60 hover:text-ink transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
              >
                <LogOut size={13}/> Log out
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login" data-testid={TID.navLogin} className={linkCls}>Log in</NavLink>
              <Link
                to="/signup"
                className="moe-btn moe-btn-primary !py-1.5 !px-4 text-[13px] whitespace-nowrap"
              >
                Start free
              </Link>
            </>
          )}
        </div>

        {/* Bell + hamburger (mobile / tablet-portrait) */}
        {user && user.role === "student" && (
          <div className="lg:hidden ml-auto mr-1">
            <NotificationBell/>
          </div>
        )}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-lg hover:bg-ink/[0.04] transition-colors"
          aria-label="Menu"
        >
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      {/* mobile menu — everything flat, nothing hidden behind a dropdown */}
      {open && (
        <div className="lg:hidden border-t border-ink/12 bg-paper">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-3.5">
            {exploreLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`${l.testid}-mobile`} className={linkCls}>
                <l.icon size={16} strokeWidth={2}/> {l.label}
              </NavLink>
            ))}
            <NavLink to="/pricing" onClick={() => setOpen(false)} className={linkCls}>
              <DollarSign size={16} strokeWidth={2}/> Pricing
            </NavLink>
            {user && user.role === "student" && (
              <NavLink to="/dashboard/manifest" onClick={() => setOpen(false)} className={linkCls}>
                <Users size={16}/> Cohort
              </NavLink>
            )}
            {user && user.role === "student" && (
              <NavLink to="/dashboard/moments" data-testid="nav-moments-mobile" onClick={() => setOpen(false)} className={linkCls}>
                <CalendarClock size={16}/> Assembly
              </NavLink>
            )}
            {user ? (
              <>
                {user.role === "student"       && <NavLink to="/dashboard" onClick={() => setOpen(false)} className={linkCls}><LayoutDashboard size={16}/>Dashboard</NavLink>}
                {user.role === "admin"         && <NavLink to="/admin"     onClick={() => setOpen(false)} className={linkCls}>Admin</NavLink>}
                {user.role === "case_handler"  && <NavLink to="/handler"   onClick={() => setOpen(false)} className={linkCls}>Handler</NavLink>}
                <button
                  onClick={() => { logout(); setOpen(false); }}
                  className="moe-btn moe-btn-ghost self-start"
                >
                  <LogOut size={14}/> Log out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className={linkCls}>Log in</NavLink>
                <Link to="/signup" onClick={() => setOpen(false)} className="moe-btn moe-btn-primary self-start">Start free</Link>
              </>
            )}
          </div>
        </div>
      )}

      {isInternal && (
        <div className="max-w-7xl mx-auto px-6 pb-3 text-xs text-muted">
          Internal workspace: {loc.pathname.startsWith("/admin") ? "admin" : "case handler"}
        </div>
      )}
    </nav>
    {/* Global spacer to reserve the fixed-nav's height on every page. */}
    <div aria-hidden="true" className={isInternal ? "h-[96px]" : "h-[68px]"} />
    </>
  );
}
